/**
 * ColumnFilterPopover — renders the right filter control for a `ColumnFilterSpec`.
 *
 * Generic and domain-agnostic: it switches on `spec.type` to render a text search,
 * a searchable/scrollable checklist (multiSelect), an enum checklist with colored
 * status pills, or a dual-thumb numeric range slider. Always includes a Clear control.
 *
 * No i18n strings are hardcoded — callers pass already-translated `labels`.
 */
import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { StatusPill } from "@/components/ui/atoms/StatusPill";
import type { StatusVariant } from "@/components/ui/atoms/StatusPill/StatusPill";
import { SearchInput } from "@/components/ui/search-input";
import { Button } from "@/components/ui/atoms/Button";
import type {
  ColumnFilterOption,
  ColumnFilterRangeValue,
  ColumnFilterSpec,
  ColumnFilterValue,
} from "./columnFilter.types";
import { foldAccents } from "./columnFilter.filterFns";

export interface ColumnFilterPopoverLabels {
  /** Placeholder for the text/search inputs. */
  searchPlaceholder: string;
  /** "Clear" control label. */
  clear: string;
  /** Empty-options fallback (e.g. "No options"). */
  noOptions: string;
}

export interface ColumnFilterPopoverProps {
  spec: ColumnFilterSpec;
  value: ColumnFilterValue | undefined;
  onChange: (next: ColumnFilterValue | undefined) => void;
  onClear: () => void;
  labels: ColumnFilterPopoverLabels;
}

const checkboxRowClass =
  "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm text-dark-invirtus hover:bg-dark-invirtus-soft/50 transition-colors cursor-pointer";

function Checklist({
  options,
  selected,
  onToggle,
  renderOptionLabel,
  searchPlaceholder,
  noOptions,
  searchable,
}: {
  options: ColumnFilterOption[];
  selected: string[];
  onToggle: (value: string) => void;
  renderOptionLabel: (opt: ColumnFilterOption) => React.ReactNode;
  searchPlaceholder: string;
  noOptions: string;
  searchable: boolean;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = foldAccents(query);
    return options.filter((o) => foldAccents(o.label).includes(q));
  }, [options, query]);

  return (
    <div className="flex flex-col gap-2">
      {searchable && options.length > 6 && (
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder={searchPlaceholder}
          size="sm"
        />
      )}
      <div className="max-h-56 overflow-y-auto pr-0.5">
        {filtered.length === 0 ? (
          <p className="px-2 py-3 text-center text-xs text-dark-invirtus/50 font-body">
            {noOptions}
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {filtered.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <li key={opt.value}>
                  <label className={checkboxRowClass}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(opt.value)}
                      className="h-4 w-4 shrink-0 rounded border-light-lila text-purple accent-purple focus:ring-purple/40"
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {renderOptionLabel(opt)}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export const ColumnFilterPopover: React.FC<ColumnFilterPopoverProps> = ({
  spec,
  value,
  onChange,
  onClear,
  labels,
}) => {
  // ── text ──────────────────────────────────────────────────────────────────
  if (spec.type === "text") {
    const current = (value as string | undefined) ?? "";
    return (
      <div className="flex w-64 flex-col gap-2 p-3">
        <SearchInput
          value={current}
          onChange={(v) => onChange(v)}
          placeholder={labels.searchPlaceholder}
          size="sm"
          autoFocus
        />
        <ClearButton label={labels.clear} onClear={onClear} />
      </div>
    );
  }

  // ── multiSelect / enum ──────────────────────────────────────────────────────
  if (spec.type === "multiSelect" || spec.type === "enum") {
    const selected = (value as string[] | undefined) ?? [];
    const options = spec.options ?? [];
    const toggle = (v: string) => {
      const next = selected.includes(v)
        ? selected.filter((s) => s !== v)
        : [...selected, v];
      onChange(next.length ? next : undefined);
    };
    return (
      <div className="flex w-60 flex-col gap-2 p-3">
        <Checklist
          options={options}
          selected={selected}
          onToggle={toggle}
          searchable={spec.type === "multiSelect"}
          searchPlaceholder={labels.searchPlaceholder}
          noOptions={labels.noOptions}
          renderOptionLabel={(opt) =>
            spec.type === "enum" && opt.color ? (
              <StatusPill variant={opt.color as StatusVariant}>
                {opt.label}
              </StatusPill>
            ) : (
              <span className="font-body">{opt.label}</span>
            )
          }
        />
        <ClearButton label={labels.clear} onClear={onClear} />
      </div>
    );
  }

  // ── range ──────────────────────────────────────────────────────────────────
  const min = spec.min ?? 0;
  const max = spec.max ?? 100;
  const range = (value as ColumnFilterRangeValue | undefined) ?? { min, max };
  const unit = spec.unit ? ` ${spec.unit}` : "";
  return (
    <div className="flex w-72 flex-col gap-4 p-4">
      <div className="flex items-center justify-between text-sm font-medium text-dark-invirtus font-body tabular-nums">
        <span>
          {range.min}
          {unit}
        </span>
        <span className="text-dark-invirtus/40">–</span>
        <span>
          {range.max}
          {unit}
        </span>
      </div>
      <Slider
        min={min}
        max={max}
        step={1}
        value={[range.min, range.max]}
        onValueChange={(vals) => {
          const [lo, hi] = vals;
          const next: ColumnFilterRangeValue = { min: lo, max: hi };
          onChange(lo <= min && hi >= max ? undefined : next);
        }}
        className="my-1"
      />
      <ClearButton label={labels.clear} onClear={onClear} />
    </div>
  );
};

function ClearButton({
  label,
  onClear,
}: {
  label: string;
  onClear: () => void;
}) {
  return (
    <Button
      type="text"
      intent="muted"
      size="xs"
      label={label}
      onClick={onClear}
      className="self-end"
    />
  );
}

ColumnFilterPopover.displayName = "ColumnFilterPopover";
